import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import SlotView from './SlotView';

interface SlotTokenAttrs {
  slotName?: string | null;
  varName?: string | null;
  varType?: string | null;
}

export const slotTokenText = (attrs: SlotTokenAttrs): string => {
  const isSystem = attrs.varType === 'system';
  const varName = isSystem ? attrs.varName : attrs.slotName;

  if (!varName) return '';
  if (varName === 'messageHistory') {
    return `\${sessionContext.system.${varName}}`;
  }
  if (isSystem) {
    return `\${chatbotContext.system.${varName}}`;
  }
  if (attrs.varType === 'global') {
    return `\${sessionContext.global.${varName}}`;
  }
  const escapedName = varName.replace(/'/g, "\\'");
  return `\${intentContext.slots['${escapedName}']}`;
};

const SLOT_TOKEN_SHELL = /^\$\{[^}]+\}/;
const QUOTED_SLOT = /^intentContext\.slots\['(.*)'\]$/;
const DOTTED_SLOT = /^intentContext\.slots\.(.+)$/;

const VARIABLE_PREFIXES = [
  { prefix: 'chatbotContext.system.', varType: 'system' },
  { prefix: 'sessionContext.system.', varType: 'system' },
  { prefix: 'sessionContext.global.', varType: 'global' }
] as const;

const tokenAttrs = (name: string, varType: string): SlotTokenAttrs => ({
  varName: name,
  slotName: name,
  varType
});

export const slotTokenAttrsFromText = (
  tokenText: string
): SlotTokenAttrs | null => {
  const shell = SLOT_TOKEN_SHELL.exec(tokenText);
  if (!shell) return null;

  const body = shell[0].slice('${'.length, -'}'.length);

  const variable = VARIABLE_PREFIXES.find(({ prefix }) =>
    body.startsWith(prefix)
  );
  if (variable) {
    return tokenAttrs(body.slice(variable.prefix.length), variable.varType);
  }

  const quoted = QUOTED_SLOT.exec(body);
  if (quoted) {
    return tokenAttrs(quoted[1].replace(/\\'/g, "'"), 'slot');
  }

  const dotted = DOTTED_SLOT.exec(body);
  return dotted ? tokenAttrs(dotted[1], 'slot') : null;
};

const SLOT_TOKEN_GLOBAL = /\$\{[^}]+\}/g;

const slotChipSpan = ({ varName, varType }: SlotTokenAttrs): string =>
  `<span data-type="slotToken" slotname="${varName}" varname="${varName}" vartype="${varType}" class="slot-chip">${varName}</span>`;

export const replaceSlotTokensWithSpans = (text: string): string =>
  text.replace(SLOT_TOKEN_GLOBAL, (match) => {
    const attrs = slotTokenAttrsFromText(match);
    return attrs ? slotChipSpan(attrs) : match;
  });

export default Node.create({
  name: 'slotToken',
  group: 'inline',
  inline: true,
  atom: true,

  addAttributes() {
    return {
      slotName: {
        default: null
      },
      varName: {
        default: null
      },
      varType: {
        default: 'slot'
      }
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-type="slotToken"]'
      }
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const isSystem = node.attrs.varType === 'system';
    const varName = isSystem
      ? (node.attrs.varName as string)
      : (node.attrs.slotName as string);

    if (!varName) return ['span', mergeAttributes(HTMLAttributes), ''];

    const tokenText = slotTokenText(node.attrs);

    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'slotToken',
        vartype: node.attrs.varType,
        varname: varName,
        class: 'slot-chip'
      }),
      tokenText
    ];
  },

  renderMarkdown(node): string {
    return slotTokenText(node.attrs as SlotTokenAttrs);
  },

  markdownTokenizer: {
    name: 'slotToken',
    level: 'inline',
    start: (src: string) => src.indexOf('${'),
    tokenize(src: string) {
      const shell = SLOT_TOKEN_SHELL.exec(src);
      if (!shell || !slotTokenAttrsFromText(shell[0])) return undefined;
      return { type: 'slotToken', raw: shell[0] };
    }
  },

  parseMarkdown(token) {
    const attrs = slotTokenAttrsFromText(token.raw ?? '');
    return attrs ? { type: 'slotToken', attrs } : [];
  },

  renderText({ node }) {
    return slotTokenText(node.attrs);
  },

  addNodeView() {
    return ReactNodeViewRenderer(SlotView);
  }
});
